"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.comparePinItem = exports.AssignmentChangeReport = void 0;
const lodash_1 = require("lodash");
const utils_1 = require("../../utils");
const entries_1 = require("../entries");
const AssignmentMeta_1 = require("./AssignmentMeta");
const AssignmentUtils_1 = require("./AssignmentUtils");
const pins_shared_1 = require("@lessonup/pins-shared");
const AssignmentComplete_1 = require("./AssignmentComplete");
const AssignmentContent_1 = require("./AssignmentContent");
// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging
class AssignmentChangeReport {
    static compareAssignments(old, updated) {
        const isPinIdEqual = (l, r) => (0, lodash_1.isEqual)(l._id, r._id);
        const titleChanged = !(0, lodash_1.isEqual)(old.assignment.name, updated.assignment.name);
        const paletChanged = !(0, lodash_1.isEqual)(old.assignment.diff, updated.assignment.diff);
        const newPins = AssignmentUtils_1.AssignmentUtils.pins(updated.assignment);
        const oldPins = AssignmentUtils_1.AssignmentUtils.pins(old.assignment);
        const addedPins = (0, lodash_1.differenceWith)(newPins, oldPins || [], isPinIdEqual);
        const removedPins = (0, lodash_1.differenceWith)(oldPins, newPins || [], isPinIdEqual);
        const possibleUpdatedPins = (0, lodash_1.intersectionWith)(oldPins, newPins || [], isPinIdEqual);
        // check updated pins
        const pinReports = (0, lodash_1.compact)(possibleUpdatedPins.map((oldPin) => {
            const pinId = oldPin._id;
            const pinsAddedBefore = (0, lodash_1.filter)(addedPins, (p) => (0, pins_shared_1.getGroupFromOrder)(p.order) <= (0, pins_shared_1.getGroupFromOrder)(oldPin.order) // (p.order || 0) % 1000 <= (oldPin.order || 0) % 1000
            ).length;
            const pinsRemovedBefore = (0, lodash_1.filter)(removedPins, (p) => (0, pins_shared_1.getGroupFromOrder)(p.order) <= (0, pins_shared_1.getGroupFromOrder)(oldPin.order) //(p.order || 0) % 1000 <= (oldPin.order || 0) % 1000
            ).length;
            const newPin = AssignmentUtils_1.AssignmentUtils.pin(updated.assignment, oldPin._id);
            const expectedOrderChange = pinsAddedBefore - pinsRemovedBefore;
            return (newPin &&
                comparePin({ pin: oldPin, answers: AssignmentMeta_1.AssignmentMeta.correctAnswerForPin(old.meta, pinId) }, { pin: newPin, answers: AssignmentMeta_1.AssignmentMeta.correctAnswerForPin(updated.meta, pinId) }, expectedOrderChange));
        }));
        const orderChanged = (0, lodash_1.some)(pinReports, (r) => r.orderChanged);
        const changeCount = (0, lodash_1.filter)([titleChanged, paletChanged, orderChanged]).length +
            (0, lodash_1.sumBy)(pinReports, (r) => r.changeCount) +
            (0, lodash_1.size)(addedPins) +
            (0, lodash_1.size)(removedPins);
        //Filter out changes to only the diff settings
        const isContentChange = (r) => r.changeCount > 0 && !(r.changeCount === 1 && r.diffChanged);
        return {
            addedPins,
            changedPins: (0, lodash_1.filter)(pinReports, isContentChange),
            unchangedPins: (0, lodash_1.filter)(pinReports, (r) => r.changeCount == 0),
            removedPins,
            diffChanged: (0, lodash_1.some)(pinReports, (r) => r.diffChanged),
            titleChanged,
            paletChanged,
            changeCount: changeCount > 1 && orderChanged ? changeCount - 1 : changeCount,
            // if there are other changes, ignore the order change.
            orderChanged: orderChanged && changeCount == 1,
            oldAssignmentData: old,
            newAssignmentData: updated,
        };
    }
    static compareAssignmentWithLesson(oldData, lessonData) {
        const options = AssignmentContent_1.AssignmentContent.optionsFromContent(oldData);
        const updatedData = AssignmentComplete_1.AssignmentComplete.updateWithLesson(oldData.type, oldData, lessonData, options, oldData.settings);
        return AssignmentChangeReport.compareAssignments((0, utils_1.omitDeepUndefined)(AssignmentComplete_1.AssignmentComplete.asAssignmentData(oldData)), (0, utils_1.omitDeepUndefined)(AssignmentComplete_1.AssignmentComplete.asAssignmentData(updatedData)));
    }
    static destructivePins(report, entries) {
        const destructiveRemovedPins = report.removedPins.filter((pin) => {
            const pinEntries = entries.map((e) => entries_1.AssignmentEntry.entryForPin(e, pin._id));
            return entriesContainDestructiveData((0, lodash_1.compact)(pinEntries));
        });
        const destructiveChangedPins = report.changedPins
            .filter((r) => {
            if (!r.destructiveChanges) {
                return false;
            }
            const pinEntries = entries.map((e) => entries_1.AssignmentEntry.entryForPin(e, r.oldPin._id));
            return entriesContainDestructiveData((0, lodash_1.compact)(pinEntries));
        })
            .map((r) => r.oldPin);
        return (0, lodash_1.merge)(destructiveChangedPins, destructiveRemovedPins);
    }
}
exports.AssignmentChangeReport = AssignmentChangeReport;
AssignmentChangeReport.isPinChangeDestructive = (report, pinId, entries) => {
    return (0, lodash_1.some)(AssignmentChangeReport.destructivePins(report, entries), (p) => p._id == pinId);
};
/**
 * Compares AssignmentPin's for (breaking) changes
 * @param entries all user entries for this specific pin
 */
function comparePin(oldPinData, newPinData, expectedOrderChange) {
    const itemReport = comparePinItem(oldPinData.pin, newPinData.pin);
    const diffChanged = !(0, lodash_1.isEqual)(oldPinData.pin.settings.differentiation, newPinData.pin.settings.differentiation);
    const answerChanged = !(0, lodash_1.isEqual)(oldPinData.answers, newPinData.answers);
    // TODO: Order has changed to fractional-jittered string
    // const orderChanged = !_.isEqual((oldPinData.pin.order || 0) + expectedOrderChange, newPinData.pin.order || 0);
    const orderChanged = true;
    console.warn('Order comparison has not been fully implemented yet.');
    return {
        // can only be destructive when there are actual user answers
        destructiveChanges: answerChanged || itemReport == 'destructive',
        orderChanged,
        diffChanged,
        newPin: newPinData.pin,
        oldPin: oldPinData.pin,
        changeCount: (0, lodash_1.filter)([answerChanged, diffChanged, itemReport != 'unchanged']).length,
    };
}
/**
 * Compares the pin.item and pin.item.custom for changes
 */
function comparePinItem(oldItem, newItem) {
    // TODO: we probably need to extract the correct properties for each type
    const propsToCompare = (pin) => {
        // this was compared in the 1.0
        // return {
        //   name: item.name, // is now in settings
        //   url: item.url, // is now in settings
        //   image: item.image, // is now in settings
        //   exclusions: item.exclusions, is now in exclusions
        //   content: item.custom, // is now in settings
        //   components: item.components, // is now in pinComponents
        // }
        return [pin.pinComponents, pin.settings, pin.studentSettings, pin.settings.differentiation];
    };
    const changed = !(0, lodash_1.isEqualWith)(propsToCompare(oldItem), propsToCompare(newItem), (a, b) => {
        // we need to replace all numbers because firebase can only store numbers with 1 smaller precision than javascript
        if ((0, lodash_1.isNumber)(a) && (0, lodash_1.isNumber)(b)) {
            return (0, lodash_1.round)(a, 10) == (0, lodash_1.round)(b, 10);
        }
        return undefined;
    });
    let isSafe = true;
    const validationOptions = (settings) => {
        return {
            caseSensitive: settings.caseSensitive,
            diacriticSensitive: settings.diacriticSensitive,
        };
    };
    if (newItem.type === 'OPEN_QUESTION' && oldItem.type === 'OPEN_QUESTION') {
        isSafe =
            (0, lodash_1.isEqual)(validationOptions(newItem.settings), validationOptions(oldItem.settings)) &&
                // TODO: not sure if we also should include the teacher settings for the "correct answer"
                // for that we might need another type than AssignmentPin (TeacherAssignmentPin?)
                // compareString(newItem.question, rightItem.question) > 60 &&
                (0, utils_1.compareString)(newItem.settings.explanation, oldItem.settings.explanation) > 60;
    }
    else if (newItem.type === 'WORD_CLOUD' && oldItem.type === 'WORD_CLOUD') {
        isSafe = (0, utils_1.compareString)(newItem.settings.word ?? undefined, oldItem.settings.word ?? undefined) > 60;
    }
    return !isSafe ? 'destructive' : changed ? 'changed' : 'unchanged';
}
exports.comparePinItem = comparePinItem;
function entriesContainDestructiveData(entries) {
    return (0, lodash_1.some)(entries, (entry) => entry && (entry.done || !(0, lodash_1.isEmpty)(entry['answer'])));
}
//# sourceMappingURL=AssignmentChangeReport.js.map